/* eslint-disable no-bitwise */
import type { OperatingSystem } from '@studio/react-utils/user-agent';
import {
  isJsonObject,
  isNotEmpty,
  parseJson,
  toEnum,
  toJson,
  toJsonObject,
  toNumber,
  toObjectEntries,
  toObjectKeys
} from '@tryiris-ai/js-types';
import type { T_JsonObject, T_Maybe, T_Optional, T_RequiredKeys } from '@tryiris-ai/js-types';
import { uniq } from 'lodash-es';
import {
  DefaultHotKeys,
  Glyphs,
  ModalScope,
  Modifiers,
  PopperScope,
  ReservedTags
} from './constants.js';
import type { Action, Combo, Directive, Fingerprint, Formula, HotKeys, KeyCode } from './types.js';
import { Category, Modifier } from './types.js';

/** @internal */
const toMask = (obj: T_JsonObject): number =>
  'modifiers' in obj
    ? toNumber(obj.modifiers, { default: 0 })
    : (obj.altKey ? Modifier.Option : 0) |
      (obj.ctrlKey ? Modifier.Control : 0) |
      (obj.metaKey ? Modifier.Command : 0) |
      (obj.shiftKey ? Modifier.Shift : 0);

/**
 * Deserializes hot key data from preference storage.
 *
 * @internal
 */
export const deserialize = (data?: unknown): HotKeys => {
  const json = parseJson(data);

  const hotKeys: HotKeys = {
    ...DefaultHotKeys
  };

  // Run through each supported action and see if there is an associated hot key.
  if (isJsonObject(json)) {
    toObjectKeys(DefaultHotKeys).forEach((action) => {
      const value = json[action];

      // Remove default hot keys that explicitly marked as `false`.
      if (value === false) {
        delete hotKeys[action];

        return;
      }

      const combo = toCombo(toJsonObject(json[action]));

      // Override default with user's preferred value.
      if (combo) {
        hotKeys[action] = combo;
      }
    });
  }

  return hotKeys;
};

/**
 * Determines if two key combinations are equal.
 *
 * @param first - First key combination.
 * @param second - Second key combination.
 * @returns `true` if the key combinations are equal, `false` otherwise.
 */
export const equalTo = (first: Combo, second: Combo): boolean =>
  toFingerprint(first) === toFingerprint(second);

/**
 * Evaluate the specified keyboard event and determine if it matches any hot key.
 *
 * @param formula - Action mappings.
 * @param event - The keyboard event to evaluated.
 * @returns The matching directive, or `undefined` if no match was found.
 */
export const evaluate = (formula: Formula, event: KeyboardEvent): T_Maybe<Directive> => {
  const combo = toCombo(event);

  if (!combo) {
    return undefined;
  }

  const action = find(formula, combo);

  return action ? { command: action, data: event } : undefined;
};

/**
 * Given an `Formula` and a `Combo`, find the `Action` (if any) that should be dispatched.
 *
 * @param formula - Action mappings.
 * @param combo - Hot key combo.
 * @returns Matching `Action` or `undefined` if no match was found.
 */
export function find(formula: Formula, combo: Combo): T_Maybe<Action> {
  const fingerprint = toFingerprint(combo);

  return fingerprint ? formula[fingerprint] : undefined;
}

/**
 * Determines if the target of the specified keyboard event is associated with an
 * interactive/reserved element or sits inside an open modal dialog.
 *
 * @param event - Keyboard event.
 * @returns `true` if the target is reserved, `false` otherwise.
 */
export const isReserved = (event: KeyboardEvent): boolean => {
  const { target } = event;

  if (!target || !('matches' in target) || typeof target.matches !== 'function') {
    return false;
  }

  const element = <Element>target;

  if (element.matches(ReservedTags)) {
    return true;
  }

  // Keys pressed inside an open modal belong to it, e.g. Space activates its button.
  const surface = element.closest(ModalScope);

  if (!surface) {
    return false;
  }

  if (!surface.closest(PopperScope)) {
    return true;
  }

  // A popover (nested or portaled) opened while a modal is open still belongs to that modal.
  return [...element.ownerDocument.querySelectorAll(ModalScope)].some(
    (modal) => !modal.closest(PopperScope)
  );
};

/**
 * Serializes hot key data to preference storage.
 *
 * @internal
 */
export const serialize = (hotKeys: HotKeys): T_Maybe<string> => {
  // We only want to serialize non-default values.
  const custom = Object.fromEntries(
    toObjectEntries(hotKeys).filter(
      ([action, combo]) => combo === false || !equalTo(combo, DefaultHotKeys[action])
    )
  );

  return isNotEmpty(custom) ? toJson(custom) : undefined;
};

/**
 * Transforms the specified value to a `Category` enumeration.
 *
 * @param value - The value to be transformed.
 * @returns The transformed `Category` enumeration or `undefined`.
 */
export const toCategory = (value: unknown): T_Maybe<Category> => toEnum(Category, value);

/**
 * Transforms the specified object to a `Combo` record.
 *
 * @param value - The object to be transformed.
 * @returns The transformed `Combo` record or `undefined`.
 */
export function toCombo(value: unknown): T_Maybe<Combo> {
  const obj = value ? toJsonObject(value) : undefined;

  if (!obj) {
    return undefined;
  }

  const keyCode: T_Maybe<KeyCode> = toNumber(obj.keyCode);

  if (!keyCode || !Glyphs[keyCode]) {
    return undefined;
  }

  let mask = toMask(obj);

  // A hotkey cannot have both `Control` and `Command` modifiers.
  if (mask & Modifier.Control && mask & Modifier.Command) {
    mask &= ~Modifier.Command;
  }

  return {
    keyCode,
    modifiers: mask
  };
}

/**
 * Returns the `Fingerprint` of the specified `Combo`/`KeyboardEvent` object.
 *
 * @param value - Keyboard event or combo.
 * @returns Fingerprint or `undefined` if an unmapped key code (`KeyCode`) was provided.
 */
export function toFingerprint(
  value: Combo | T_RequiredKeys<T_Optional<KeyboardEvent>, 'keyCode'>
): T_Maybe<Fingerprint> {
  const glyph = Glyphs[value.keyCode];

  if (!glyph) {
    return undefined;
  }

  const mask = toMask(<T_JsonObject>value);

  return [
    Glyphs[value.keyCode],
    mask & (Modifier.Control | Modifier.Command) ? '1' : '0',
    mask & Modifier.Option ? '1' : '0',
    mask & Modifier.Shift ? '1' : '0'
  ].join(':');
}

/**
 * Transforms the specified `HotKeys` into an `Formula`.
 *
 * @param hotKeys - Hot keys to be transformed.
 * @returns Corresponding formula.
 */
export const toFormula = (hotKeys: T_Optional<HotKeys>): Formula => {
  const formula: Formula = {};

  toObjectEntries(hotKeys).forEach(([action, combo]) => {
    const fingerprint = combo ? toFingerprint(combo) : false;

    if (!fingerprint) {
      return;
    }

    formula[fingerprint] = action;
  });

  return formula;
};

/**
 * Convert a hot key combo to its individual glyphs.
 *
 * @param os - Operating system.
 * @param combo - Key combo.
 * @returns Array of glyphs or `undefined` if an unmapped key code (`KeyCode`) was provided.
 */
export function toGlyphs(os: OperatingSystem, combo: Combo): T_Maybe<string[]> {
  const { keyCode } = combo;

  const modifiers = Modifiers[os];
  const glyph = Glyphs[keyCode];

  if (!glyph) {
    return undefined;
  }

  return [
    ...uniq(modifiers.order.filter((µ) => combo.modifiers & µ).map((µ) => modifiers.glyphs[µ])),
    glyph
  ];
}

/**
 * Convert a hot key combo to its string equivalent.
 *
 * @param os - Operating system.
 * @param combo - Key combo.
 * @returns String representation of key combo or `undefined` if an unmapped key code (`KeyCode`)
 *   was provided.
 */
export const toString = (os: OperatingSystem, combo: Combo): T_Maybe<string> => {
  const glyphs = toGlyphs(os, combo);

  if (!glyphs?.length) {
    return undefined;
  }

  return glyphs.join(Modifiers[os].separator);
};
